<template>
    <hox-modal class="importExportModal" @close="close">
        <template #header>Import/Export {{ headerTabName }}</template>

        <div>
            <navigation-tabs size="small" class="importExportModal__navigation">
                <navigation-tabs-item :is-active="activeTab === 'content'" @click="onClickSectionTab('content')">
                    Content
                </navigation-tabs-item>
                <navigation-tabs-item
                    :is-active="activeTab === 'translations'"
                    @click="onClickSectionTab('translations')"
                >
                    Translations
                </navigation-tabs-item>
            </navigation-tabs>

            <template v-if="activeTab === 'content'">
                <hox-alert type="info" class="importExportModal__howto">
                    <template #title>To upload content</template>
                    <template #content>
                        <ol class="importExportModal__howto-list">
                            <li>
                                Download template for
                                <span class="link-like" @click="downloadTemplate('global')">global</span>
                                or
                                <span
                                    class="link-like"
                                    data-testid="import-export-modal__download-template-specific-link"
                                    @click="downloadTemplate('template')"
                                >
                                    template specific
                                </span>
                                values
                            </li>
                            <li>
                                Fill in the content in the the file
                                <br />
                                Note: Import supports text and file based editables only
                            </li>
                            <li>Upload the content XSLX file</li>
                            <li>Review and save changes in the editor</li>
                        </ol>
                    </template>
                </hox-alert>

                <div class="importExportModal__upload-controls">
                    <xls-upload-button
                        @click.stop="valuesUploadCompleted = false"
                        @load="onXlsValuesLoad"
                        @error="onValuesImportError"
                    >
                        <Button type="primary" class="">Upload Values</Button>
                    </xls-upload-button>
                </div>
                <div class="importExportModal__upload-results">
                    <hox-alert v-if="hasValuesError" type="danger" class="importExportModal__upload-result">
                        <template #content>
                            <p>{{ valuesErrorMessage }}</p>
                            <ul v-if="valuesErrorDetails" class="importExportModal__upload-results-error-list">
                                <li v-for="(detail, i) in valuesErrorDetails" :key="i">
                                    {{ detail }}
                                </li>
                            </ul>
                        </template>
                    </hox-alert>

                    <hox-alert
                        v-if="valuesUploadCompleted && !hasValuesError"
                        type="success"
                        class="importExportModal__upload-result"
                    >
                        <template #title>File uploaded successfully</template>
                        <template #content>
                            <p>Content has been imported. Please review and save the changes.</p>
                        </template>
                    </hox-alert>
                </div>
            </template>
        </div>

        <template v-if="activeTab === 'translations'">
            <template v-if="!languageUploadCompleted">
                <hox-alert type="info" class="importExportModal__howto">
                    <template #title>To create the translations</template>
                    <template #content>
                        <ol class="importExportModal__howto-list">
                            <li>Download the file for the language you want to populate</li>
                            <li>Fill in the translations in the file</li>
                            <li>Upload the translations XSLX file</li>
                            <li>Review and save changes in the editor</li>
                        </ol>
                    </template>
                </hox-alert>
                <div class="importExportModal__files">
                    <h3>Download translation file</h3>
                    <div v-for="lang in campaignLanguages" :key="lang" class="link-like" @click="onClick(lang)">
                        {{ languageLabel(lang) }}
                    </div>
                </div>
            </template>

            <div class="importExportModal__upload-results">
                <hox-alert v-if="hasTranslationsError" type="danger" class="importExportModal__upload-result">
                    <template #content>
                        <p>{{ translationsErrorMessage }}</p>
                    </template>
                </hox-alert>
                <hox-alert v-if="languageUploadCompleted" type="success" class="importExportModal__upload-result">
                    <template #title>File uploaded successfully</template>
                    <template #content>
                        <p>
                            {{ languageImportFeedback?.message }}
                        </p>
                        <p v-if="languageImportFeedback?.newLanguages">
                            {{ languageImportFeedback?.newLanguages }}
                        </p>
                    </template>
                    <template v-if="languageImportFeedback?.languages?.length === 1" #actionItems>
                        <Button
                            v-if="onlyImportedLanguage !== selectedLanguage"
                            type="primary"
                            @click="switchAndClose(onlyImportedLanguage)"
                        >
                            Switch to {{ languageLabel(onlyImportedLanguage) }} and close
                        </Button>
                    </template>
                </hox-alert>
            </div>

            <xls-upload-button
                @click.stop="valuesUploadCompleted = false"
                @load="onXlsTranslationsLoad"
                @error="onTranslationsError"
            >
                <Button type="primary">Upload Translations</Button>
            </xls-upload-button>
        </template>

        <template #footer>
            <div class="share-preview-modal__footer-button-wrapper">
                <Button @click="close">Close</Button>
            </div>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { Editable, Overwrite } from "shared-types";
import { EditableType } from "shared-utils/enums/editables";
import { OverwriteScope } from "shared-utils/enums/overwrites";

import * as editableUtils from "@/utils/editables";
import HoxModal from "@/components/Modal/Modal/Modal.vue";
import { camelToUpperCase } from "@/utils";
import { ExportGroupScopeLabel } from "@/enums/overwrites";
import { EditorAction, EditorGetters } from "@/store/modules/editor";
import NavigationTabs from "@/components/common/NavigationTabs/Container.vue";
import NavigationTabsItem from "@/components/common/NavigationTabs/Tab.vue";
import { campaignDefaultLanguage, CampaignGetters } from "@/store/modules/campaign";
import { ExportableEditableTypes } from "@/enums/editables";
import ImportExport, { getExportValueFromOverwrite } from "@/services/ImportExport";
import XlsUploadButton from "@/components/Campaign/XlsUploadButton.vue";
import type { EditableOverwrite } from "@/types/types";
import { useOverwrite } from "@/composables/useOverwrite";
import { languageCodes, localeToFriendlyName } from "@/utils/languages";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";

const emptyOverwriteImportValue = "{empty}";
type ImportSheetRow = unknown[];
type ImportedWorkbook = Record<string, { sheetData?: ImportSheetRow[] }> & { sheets: string[] };
type ProcessingErrorDetail = { message: string };
type GroupValueImportRow = {
    groupValueName: string;
    masterTemplateName: string;
    editableValues: (string | null)[];
    groupValueId?: string;
};

class GroupProcessingError extends Error {
    groupName: string;
    details: ProcessingErrorDetail[];

    constructor(message: string, groupName: string, details: ProcessingErrorDetail[]) {
        super(message);
        this.name = "GroupProcessingError";
        this.groupName = groupName;
        this.details = details;
    }
}

export default defineComponent({
    name: "ImportExportModal",
    components: {
        XlsUploadButton,
        HoxModal,
        NavigationTabs,
        NavigationTabsItem
    },

    props: {
        createNewGroupValues: {
            type: Function
        },
        languageImportComplete: {
            type: Boolean,
            default: false
        },
        languageImportFeedback: {
            type: Object
        }
    },

    emits: ["languagesUpdated", "close", "exportValuesTemplate", "exportLanguage"],

    setup() {
        const { getSavedOverwrite } = useOverwrite();
        const ga4 = useGA4Tracking();

        return {
            getSavedOverwrite,
            ga4
        };
    },

    data() {
        return {
            hasValuesError: false,
            hasTranslationsError: false,
            translationsErrorMessage: "",
            valuesErrorMessage: "",
            valuesErrorDetails: [] as ProcessingErrorDetail[],
            languageUploadCompleted: false,
            valuesUploadCompleted: false,
            activeTab: "content",
            // eslint-disable-next-line vue/no-unused-properties
            resizeSettings: { cover: undefined },
            importExportService: null as ImportExport | null
        };
    },

    computed: {
        campaignLanguages() {
            return this.$store.state.campaign.languages;
        },

        editableIds() {
            return this.$store.state.campaign.normalized.editableIds;
        },

        editables() {
            return this.$store.state.campaign.normalized.editables;
        },

        editablesGroupNames() {
            return this.$store.state.campaign.normalized.editableGroupIds;
        },

        editablesNameMap() {
            const map: Record<string, string> = {};
            this.editableIds.forEach(editableId => {
                if (this.editables[editableId] && ExportableEditableTypes.includes(this.editables[editableId].type)) {
                    map[this.editables[editableId].name] = editableId;
                }
            });

            return map;
        },

        headerTabName() {
            return camelToUpperCase(this.activeTab);
        },

        masterTemplateNameMap() {
            return this.$store.getters[CampaignGetters.masterTemplatesBySize];
        },

        onlyImportedLanguage() {
            if (this.languageImportFeedback?.languages && this.languageImportFeedback.languages.length === 1) {
                return this.languageImportFeedback.languages[0];
            }

            return null;
        },

        selectedLanguage() {
            return this.$store.state.editor.selectedLanguage;
        }
    },

    watch: {
        languageImportComplete(val) {
            this.languageUploadCompleted = val;
        }
    },

    created() {
        this.importExportService = new ImportExport(this.$apollo as never, this.$store);
        this.ga4.trackFeatureEvent({
            featureEvent: GA4.EVENT_TYPES.FEATURE_ACTIVATE,
            featureCategory: GA4.FEATURE_CATEGORIES.IMPORT_EXPORT_ASSEMBLE,
            featureContext: GA4.FEATURE_CONTEXTS.IMPORT_EXPORT_ASSEMBLE
        });
    },

    unmounted() {
        this.ga4.trackFeatureEvent({
            featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
            featureCategory: GA4.FEATURE_CATEGORIES.IMPORT_EXPORT_ASSEMBLE,
            featureContext: GA4.FEATURE_CONTEXTS.IMPORT_EXPORT_ASSEMBLE
        });
    },

    methods: {
        async onXlsTranslationsLoad(importedData: ImportedWorkbook) {
            this.languageUploadCompleted = false;
            this.hasTranslationsError = false;

            if (!importedData.sheets.length) {
                this.valuesErrorMessage =
                    "The file is either empty or is incorrectly formatted. Please check your data and try again.";
                this.hasTranslationsError = true;
                return;
            }

            await this.$nextTick();

            const processingErrors: unknown[] = [];
            await Promise.all(
                importedData.sheets.map(async (sheetName: string) => {
                    try {
                        await this.processTranslationsSheet(importedData[sheetName]?.sheetData ?? []);
                    } catch (e) {
                        processingErrors.push(e);
                    }
                })
            );

            this.languageUploadCompleted = true;
        },

        processTranslationsSheet(sheetData: ImportSheetRow[]) {
            if (!sheetData.length) {
                return;
            }

            const columnIndexes: Record<string, number> = {};
            const header = (sheetData.shift() ?? []) as (string | null)[];
            header.shift(); // take "en" out of the computations
            const translationsInit: { languages: string[]; [key: string]: string[] | Record<string, string> } = {
                languages: []
            };
            const ignoredHeaders: string[] = [];
            header.forEach((langValue: string | null, index: number) => {
                if (!langValue) {
                    return;
                }
                const lang = langValue.toLowerCase();
                if (languageCodes.includes(lang as never)) {
                    translationsInit[lang] = {};
                    translationsInit.languages.push(lang);
                    columnIndexes[lang] = index;
                } else {
                    ignoredHeaders.push(langValue);
                }
            });

            if (ignoredHeaders.length) {
                this.$snackbar.info(
                    "Columns ignored",
                    `"${ignoredHeaders.join(", ")}" doesn't seem to be a valid language code`,
                    { duration: 10, closable: true }
                );
            }

            const languageData = sheetData.reduce((acc, curr: ImportSheetRow) => {
                if (curr[0]) {
                    translationsInit.languages.forEach(lang => {
                        const columnIndex = columnIndexes[lang];
                        if (columnIndex !== undefined && curr[columnIndex + 1]) {
                            (acc[lang] as Record<string, string>)[curr[0] as string] = curr[columnIndex + 1] as string;
                        }
                    });
                }

                return acc;
            }, translationsInit);

            this.$emit("languagesUpdated", {
                languageData
            });
        },

        async onXlsValuesLoad(importedData: ImportedWorkbook) {
            this.valuesUploadCompleted = false;
            this.hasValuesError = false;
            this.valuesErrorDetails = [];

            if (!importedData.sheets.length) {
                this.valuesErrorMessage =
                    "The file is either empty or is incorrectly formatted. Please check your data and try again.";
                this.hasValuesError = true;
                return;
            }

            await this.$nextTick();

            const sheetsData = importedData.sheets.map(sheetName => ({
                sheetData: importedData[sheetName]?.sheetData ?? [],
                sheetName
            }));

            await this.executeImport(sheetsData);
        },

        async executeImport(sheetsData: { sheetData: ImportSheetRow[]; sheetName: string }[]) {
            const processingErrors: GroupProcessingError[] = [];
            await Promise.all(
                sheetsData.map(async ({ sheetData, sheetName }) => {
                    try {
                        await this.processGroupImport(sheetData, sheetName);
                    } catch (e) {
                        processingErrors.push(e as GroupProcessingError);
                    }
                })
            );

            if (processingErrors.length) {
                this.hasValuesError = true;
                const processingErrorsText = processingErrors.map(({ groupName }) => groupName).join(",");
                this.valuesErrorMessage = `Upload complete but some data could not be imported: ${processingErrorsText}`;
                this.valuesErrorDetails = processingErrors.flatMap(({ details }) => details);
            }

            this.valuesUploadCompleted = true;
        },

        async processGroupImport(dataArray: ImportSheetRow[], _groupName: string) {
            // empty value represents templateName header
            const [groupNameCell, templateHeader, ...editableNamesHeader] = (dataArray.shift() ?? []) as (
                | string
                | null
            )[];
            let allSizesImport = false;
            if ((templateHeader ?? "").toLowerCase() !== "template") {
                // if this column is not a template - we treat it as a value column and set the the value for All Sizes
                editableNamesHeader.unshift(templateHeader ?? "");
                allSizesImport = true;
            }
            const getGroupName = (paramName: string, cellName: string) => paramName || cellName.replace("*GROUP: ", "");
            const groupName = getGroupName(_groupName, String(groupNameCell ?? ""));
            const firstEmptyHeaderCell = editableNamesHeader.indexOf("");
            const lastRelevantColumn = firstEmptyHeaderCell > -1 ? firstEmptyHeaderCell : editableNamesHeader.length;

            const editableNames = editableNamesHeader
                .slice(0, lastRelevantColumn)
                .filter((editableName): editableName is string => editableName != null);

            if (!this.editablesGroupNames.includes(groupName)) {
                this.valuesErrorMessage = `Uploaded group '${groupName}' is not a valid name for the master templates available in this campaign.
                Please check your data and try again.`;
                this.hasValuesError = true;

                return;
            }
            const invalidEditableNames = editableNames.filter(
                (editableName: string) => !this.editablesNameMap[editableName]
            );

            if (invalidEditableNames.length) {
                this.valuesErrorMessage = `Uploaded file contains an editable name that is not valid for the group "${groupName}".
                Please check your data and try again.
                Invalid names: ${invalidEditableNames.join(", ")}`;
                this.hasValuesError = true;

                return;
            }

            const groupValueRows = dataArray
                // filter out empty rows
                .filter((l: ImportSheetRow) => !!l[0])
                .map(curr => this.parseGroupValueImportRow(curr, groupName, lastRelevantColumn, allSizesImport))
                .filter(Boolean) as GroupValueImportRow[];

            const groupValuesToCreateSet = groupValueRows.reduce((acc: Set<string>, grp: GroupValueImportRow) => {
                if (!grp.groupValueId && grp.groupValueName !== "") {
                    acc.add(grp.groupValueName);
                }

                return acc;
            }, new Set());

            if (groupValuesToCreateSet.size) {
                try {
                    const createdGroupValues = await this.createNewGroupValues?.(groupName, [
                        ...groupValuesToCreateSet
                    ]);
                    if (!createdGroupValues) {
                        return;
                    }
                    const createdGroupValuesIdMap = createdGroupValues
                        /* Filter out Boolean returns which indicate that there was no need to call a query */
                        .reduce((acc: Record<string, string>, curr: { value: string; _id: string }) => {
                            return Object.assign(acc, {
                                [curr.value]: curr._id
                            });
                        }, {});
                    groupValueRows.forEach((g: GroupValueImportRow) => {
                        if (!g.groupValueId) {
                            Object.assign(g, { groupValueId: createdGroupValuesIdMap[g.groupValueName] });
                        }
                    });
                } catch (e) {
                    this.valuesErrorMessage = `Failed to create group values for group ${groupName}`;
                    console.error(e);
                    this.hasValuesError = true;
                }
            }

            /* When all templates share the same value for an editable, promote it to global scope
            instead of creating individual template-specific overwrites (locks) */
            const globalizedEditables = new Set<string>();
            if (!allSizesImport) {
                this.optimizeUniformTemplatesToGlobal(groupValueRows, editableNames, globalizedEditables);
            }

            /* Because the Unsaved group scope overwrite can affect how we process the template scope overwrites we need
            to process group values first.
            Issue Example:
            For the boolean type when the default is set to false
            And we import a file which updates group to true and the template value to false
            The second value will not be applied as there is no change comparing to the saved state */

            const processingErrors: ProcessingErrorDetail[] = [];
            await Promise.all(
                groupValueRows
                    .filter((row: GroupValueImportRow) => row.masterTemplateName === ExportGroupScopeLabel)
                    .flatMap(row =>
                        row.editableValues.map(async (editableValue, columnIndex: number) => {
                            try {
                                await this.processGroupValueImportRow(row, editableNames, editableValue, columnIndex);
                            } catch (e) {
                                processingErrors.push({ message: e instanceof Error ? e.message : String(e) });
                            }
                        })
                    )
            );

            // Remove existing template-specific overwrites for values promoted to global
            if (globalizedEditables.size > 0) {
                await this.removeTemplateOverwritesForGlobalizedValues(globalizedEditables);
            }

            // going over template level overwrites
            await Promise.all(
                groupValueRows
                    .filter((row: GroupValueImportRow) => row.masterTemplateName !== ExportGroupScopeLabel)
                    .flatMap(row =>
                        row.editableValues.map(async (editableValue, columnIndex: number) => {
                            try {
                                await this.processGroupValueImportRow(row, editableNames, editableValue, columnIndex);
                            } catch (e) {
                                processingErrors.push({ message: e instanceof Error ? e.message : String(e) });
                            }
                        })
                    )
            );

            if (processingErrors.length) {
                throw new GroupProcessingError(
                    `Error when processing entries for ${groupName}`,
                    groupName,
                    processingErrors
                );
            }
        },

        close() {
            this.$emit("close");
        },

        optimizeUniformTemplatesToGlobal(
            groupValueRows: GroupValueImportRow[],
            editableNames: string[],
            globalizedEditables: Set<string>
        ) {
            const masterTemplateNames = Object.keys(this.masterTemplateNameMap);
            if (!masterTemplateNames.length) return;

            const rowsByGroupValue: Record<string, typeof groupValueRows> = {};
            groupValueRows.forEach(row => {
                if (row.masterTemplateName === ExportGroupScopeLabel || !row.groupValueId) return;
                const rowsForGroup = (rowsByGroupValue[row.groupValueId] ??= []);
                rowsForGroup.push(row);
            });

            Object.entries(rowsByGroupValue).forEach(([groupValueId, rows]) => {
                // Only optimize when all campaign templates are represented in the import
                const importedTemplateNames = new Set(rows.map(r => r.masterTemplateName));
                if (!masterTemplateNames.every(name => importedTemplateNames.has(name))) return;

                const globalEditableValues = new Array(editableNames.length).fill("");
                let hasGlobalizedValues = false;

                editableNames.forEach((editableName: string, columnIndex: number) => {
                    const values = rows.map(r => r.editableValues[columnIndex]);
                    const nonEmptyValues = values.filter(v => v !== "" && v != null);

                    // All templates must have the same non-empty value
                    if (nonEmptyValues.length === rows.length && new Set(nonEmptyValues).size === 1) {
                        globalEditableValues[columnIndex] = nonEmptyValues[0];
                        hasGlobalizedValues = true;

                        // Clear from template rows so they're skipped in phase 2
                        rows.forEach(r => {
                            r.editableValues[columnIndex] = "";
                        });

                        const editableId = this.editablesNameMap[editableName];
                        if (editableId) {
                            globalizedEditables.add(`${editableId}:${groupValueId}`);
                        }
                    }
                });

                if (hasGlobalizedValues) {
                    // Add synthetic "All Sizes" row to be processed as global in phase 1
                    groupValueRows.push({
                        groupValueName: rows[0]!.groupValueName,
                        masterTemplateName: ExportGroupScopeLabel,
                        editableValues: globalEditableValues,
                        groupValueId
                    });
                }
            });
        },

        findTemplateLockOverwrite(editableId: string, groupValueId: string, masterTemplateId: string) {
            // Scan both saved and local (unsaved) overwrites for an EditableGroupTemplate-scope lock
            const mergedEditableOverwrites = this.$store.getters[EditorGetters.editableOverwrites] as {
                editable: { _id: string };
                overwrites: Overwrite[];
            }[];

            const match = mergedEditableOverwrites.find(eo => eo.editable._id === editableId);
            if (!match) return null;

            return (
                match.overwrites.find(
                    ow =>
                        ow.scope === OverwriteScope.EditableGroupTemplate &&
                        ow.masterTemplateId === masterTemplateId &&
                        ow.language === campaignDefaultLanguage &&
                        Array.isArray(ow.editableGroupValueIds) &&
                        ow.editableGroupValueIds.length === 1 &&
                        (ow.editableGroupValueIds[0] || "") === groupValueId
                ) ?? null
            );
        },

        isImportedValueSameAsGlobal(
            editable: Editable,
            groupValueIds: string[],
            masterTemplateId: string,
            importedValue: unknown
        ) {
            const mergedEditableOverwrites = this.$store.getters[EditorGetters.editableOverwrites] as {
                editable: { _id: string };
                overwrites: Overwrite[];
            }[];
            const groupOverwrite = this.$store.getters[CampaignGetters.editableOverwriteValue](
                editable._id,
                undefined,
                campaignDefaultLanguage,
                groupValueIds,
                mergedEditableOverwrites
            ) as Overwrite;
            const globalValue = getExportValueFromOverwrite({
                editable,
                masterTemplateId,
                overwrite: groupOverwrite as EditableOverwrite,
                campaignMasterTemplateIds: this.$store.state.campaign.normalized.masterTemplateIds
            });

            let normalizedImported = importedValue === emptyOverwriteImportValue ? "" : String(importedValue ?? "");

            if (editable.type === EditableType.Boolean) {
                normalizedImported = normalizedImported.toLowerCase() === "true" ? "true" : "false";
            }

            return normalizedImported === globalValue;
        },

        async processGroupValueImportRow(
            row: GroupValueImportRow,
            editableNames: string[],
            editableValue: string | null,
            columnIndex: number
        ) {
            if (editableValue === "") {
                return Promise.resolve();
            }

            let value = editableValue;
            // this is the way we enable having empty strings in the csv
            if (editableValue === emptyOverwriteImportValue) {
                value = "";
            }

            const editableNameFromHeader = editableNames[columnIndex];
            if (!editableNameFromHeader) {
                return Promise.resolve();
            }

            const editableId = this.editablesNameMap[editableNameFromHeader] as string | undefined;
            if (!editableId) {
                return Promise.resolve();
            }

            const editable = this.editables[editableId] as Editable;
            const scope =
                row.masterTemplateName === ExportGroupScopeLabel
                    ? OverwriteScope.EditableGroup
                    : OverwriteScope.EditableGroupTemplate;

            if (scope === OverwriteScope.EditableGroup) {
                return this.setImportedValue(editable, [row.groupValueId as string], undefined, value, scope);
            }

            if (
                this.masterTemplateNameMap[row.masterTemplateName] &&
                this.masterTemplateNameMap[row.masterTemplateName].length
            ) {
                return Promise.all(
                    this.masterTemplateNameMap[row.masterTemplateName].map((masterTemplateId: string) =>
                        this.setImportedValue(editable, [row.groupValueId as string], masterTemplateId, value, scope)
                    )
                );
            }

            this.$snackbar.warning(`This campaign does not contain a template called "${row.masterTemplateName}"`);
            return Promise.resolve();
        },

        async setImportedValue(
            editable: Editable,
            groupValueIds: string[],
            masterTemplateId: string | undefined,
            importedValue: string | null | { label: string; value: string; selected?: boolean }[],
            scope: string
        ) {
            let value = importedValue;
            if (editable.type === EditableType.Array) {
                value = this.getArrayValue(editable, groupValueIds, masterTemplateId, String(importedValue));
            }

            if (scope === OverwriteScope.EditableGroupTemplate && masterTemplateId) {
                const [groupValueId] = groupValueIds;

                if (
                    groupValueId &&
                    this.isImportedValueSameAsGlobal(editable, groupValueIds, masterTemplateId, importedValue)
                ) {
                    const templateOverwrite = this.findTemplateLockOverwrite(
                        editable._id,
                        groupValueId,
                        masterTemplateId
                    );

                    if (templateOverwrite?._id) {
                        return this.$store.dispatch(EditorAction.RemoveOverwrite, templateOverwrite._id);
                    }

                    return Promise.resolve();
                }
            }

            return this.importExportService!.applyEditableUpdate({
                language: campaignDefaultLanguage,
                value,
                mediaItem: null,
                editableGroupValueIds: groupValueIds,
                editable,
                scope,
                ...(scope === OverwriteScope.EditableGroupTemplate && { masterTemplateId })
            } as never);
        },

        async removeTemplateOverwritesForGlobalizedValues(globalizedEditables: Set<string>) {
            const { masterTemplateIds } = this.$store.state.campaign.normalized;
            let overwriteIdsToRemove: string[] = [];

            for (const key of globalizedEditables) {
                const [editableId, groupValueId] = key.split(":");
                if (!editableId || !groupValueId) {
                    continue;
                }

                // Find saved template-specific overwrites to remove
                for (const masterTemplateId of masterTemplateIds) {
                    const savedOverwrite = this.getSavedOverwrite(
                        editableId,
                        masterTemplateId,
                        campaignDefaultLanguage,
                        [groupValueId]
                    );

                    if (
                        savedOverwrite &&
                        savedOverwrite._id &&
                        savedOverwrite.scope === OverwriteScope.EditableGroupTemplate
                    ) {
                        overwriteIdsToRemove.push(savedOverwrite._id);
                    }
                }

                // Find local template-specific overwrites to remove (from previous imports in same session)
                for (const editableOverwrite of this.$store.state.editor.localEditableOverwrites) {
                    if (editableOverwrite.editable._id !== editableId) continue;

                    for (const overwrite of editableOverwrite.overwrites) {
                        if (
                            overwrite.scope === OverwriteScope.EditableGroupTemplate &&
                            overwrite.language === campaignDefaultLanguage &&
                            JSON.stringify(overwrite.editableGroupValueIds) === JSON.stringify([groupValueId])
                        ) {
                            overwriteIdsToRemove.push(overwrite._id);
                        }
                    }
                }
            }

            overwriteIdsToRemove = [...new Set(overwriteIdsToRemove)];

            for (const id of overwriteIdsToRemove) {
                await this.$store.dispatch(EditorAction.RemoveOverwrite, id);
            }
        },

        downloadTemplate(type: string) {
            this.$emit("exportValuesTemplate", type);
            this.ga4.trackClickEvent({
                interactionObject: GA4.CLICK_INTERACTION_OBJECTS.LINK,
                interactionText: `Download ${type} template`
            });
        },

        getArrayValue(
            editable: Editable,
            groupValueIds: string[],
            masterTemplateId: string | undefined,
            editableValue: string
        ) {
            let defaultValue = editableUtils.getDefaultValue(editable, masterTemplateId ?? null);
            if (!defaultValue) {
                defaultValue = editableUtils.getDefaultValue(
                    editable,
                    editable.defaultValues[0]?.masterTemplateId ?? null
                );
            }

            if (!Array.isArray(defaultValue)) {
                return [];
            }

            return defaultValue.map(({ label, value }) => {
                const option: { label: string; value: string; selected?: boolean } = {
                    label,
                    value
                };

                if (option.label === editableValue) {
                    option.selected = true;
                }

                return option;
            });
        },

        languageLabel(lang: string) {
            return localeToFriendlyName(lang as never);
        },

        onClick(lang: string) {
            this.$emit("exportLanguage", lang);
            this.ga4.trackClickEvent({
                interactionObject: GA4.CLICK_INTERACTION_OBJECTS.LINK,
                interactionText: `Download ${lang} language file`
            });
        },

        onClickSectionTab(tab: string) {
            this.activeTab = tab;
            this.ga4.trackClickEvent({
                interactionObject: GA4.CLICK_INTERACTION_OBJECTS.TAB,
                interactionText: `Import/Export ${camelToUpperCase(tab)} tab`
            });
        },

        onTranslationsError(e: Error) {
            this.hasTranslationsError = true;
            this.translationsErrorMessage = e.message;
        },

        onValuesImportError(e: Error) {
            this.hasValuesError = true;
            this.valuesErrorMessage = e.message;
        },

        parseGroupValueImportRow(
            row: ImportSheetRow,
            groupName: string,
            lastRelevantColumn: number,
            allSizesImport: boolean
        ) {
            return this.importExportService!.parseGroupValueImportRow(
                row as (string | null)[],
                groupName,
                lastRelevantColumn,
                allSizesImport
            );
        },

        switchAndClose(uploadedLanguage: string) {
            this.$store.dispatch(EditorAction.SelectLanguage, uploadedLanguage);
            this.close();
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.importExportModal {
    &__navigation {
        margin-bottom: $spacing;
    }

    &__howto {
        &-list {
            padding-left: $spacing;
        }
    }

    &__files {
        padding-left: $spacing;
        margin-bottom: $spacing;
    }

    &__upload-result {
        margin-top: $spacing;
    }

    &__upload-results-error-list {
        padding-left: $spacing;
        word-break: break-word;
    }

    &__upload-controls {
        display: flex;
        width: 100%;
        justify-content: space-between;
        align-items: center;

        .labeled-switch {
            flex: 0;

            &__label {
                white-space: nowrap;
            }
        }
    }
}
</style>
